import { useEffect, useMemo, useState } from 'react'
import { trackMapViewed } from '@/lib/analytics-events'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useThreatMap, useThreatMapCoverage, useThreatMapWithCoverage } from '@/hooks/map'
import { useMapView } from '@/hooks/useMapView'
import { Globe } from '@/components/map/Globe'
import { RegionMap } from '@/components/map/RegionMap'
import { CountryMap } from '@/components/map/CountryMap'
import { SiteView } from '@/components/map/SiteView'
import { SocWallboardMode } from '@/components/map/SocWallboardMode'
import { MapToolbar } from '@/components/map/MapToolbar'
import { Breadcrumbs } from '@/components/map/Breadcrumbs'
import { StatusBar } from '@/components/map/StatusBar'
import { TopAttackersPanel } from '@/components/map/panels/TopAttackersPanel'
import { TopSectorsPanel } from '@/components/map/panels/TopSectorsPanel'
import { SeverityChartPanel } from '@/components/map/panels/SeverityChartPanel'
import { IncidentTicker } from '@/components/map/panels/IncidentTicker'
import { IncidentDrawer } from '@/components/map/drawers/IncidentDrawer'
import { ActorDrawer } from '@/components/map/drawers/ActorDrawer'
import { RansomwareActivityOverlay } from '@/components/leak-sites/RansomwareActivityOverlay'
import { REGIONS } from '@/assets/region-mapping'
import type { MapFilters, MapView, PulseRate, RegionSlug, Severity } from '@/types/map'
import { cn } from '@/lib/utils'
import '@/components/map/map-console.css'

/**
 * SP6 SOC Threat Map — polished experience.
 *
 * Wires MapToolbar, Breadcrumbs, IncidentDrawer, ActorDrawer, and search/navigation
 * handlers into a single composed page. Mounted at /map after the PR-3 route swap.
 */
export function MapNextPage() {
  const [filters, setFilters] = useState<MapFilters>({
    severities: new Set<Severity>(['critical', 'high', 'medium', 'low']),
    sectors: new Set(),
    rangeDays: 1,
  })
  // KAN-32 map:viewed
  useEffect(() => {
    trackMapViewed(filters.rangeDays)
  }, [filters.rangeDays])

  const [pulseRate, setPulseRate] = useState<PulseRate>('calm')
  const [socMode, setSocMode] = useState(false)
  const [openIncidentId, setOpenIncidentId] = useState<string | null>(null)
  const [openActorId, setOpenActorId] = useState<string | null>(null)
  // Panel collapse state — lets the user hide either side-rail so the globe
  // column expands into the freed space. Globe re-centres automatically via
  // the flex/aspect-ratio wrapper in `GlobeFrame`.
  const [leftCollapsed, setLeftCollapsed] = useState(false)
  const [rightCollapsed, setRightCollapsed] = useState(false)
  // Plan B Plan 2 T17: Ransomware activity overlay toggle. Defaults OFF so
  // the SOC threat map renders unchanged for users who don't opt in.
  const [ransomwareEnabled, setRansomwareEnabled] = useState(false)

  const { view, drillToRegion, drillToCountry, drillToSite, back, reset } = useMapView()
  const regularQuery = useThreatMap(filters.rangeDays, { enabled: !socMode })
  const combinedQuery = useThreatMapWithCoverage(filters.rangeDays, socMode)
  const separateCoverageQuery = useThreatMapCoverage(filters.rangeDays, false)

  const query = socMode ? { ...combinedQuery, data: combinedQuery.data?.map } : regularQuery
  const coverageQuery = socMode ? { ...combinedQuery, data: combinedQuery.data?.coverage } : separateCoverageQuery
  const data = query.data
  const feedAgeMs = Date.now() - (query.dataUpdatedAt ?? Date.now())

  // SOC Esc-exit.
  useEffect(() => {
    if (!socMode) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSocMode(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [socMode])

  const openIncident = useMemo(
    () =>
      data && openIncidentId ? (data.incidents.find((i) => i.id === openIncidentId) ?? null) : null,
    [data, openIncidentId],
  )
  const openActor = useMemo(
    () => (data && openActorId ? (data.actors.find((a) => a.id === openActorId) ?? null) : null),
    [data, openActorId],
  )

  function handleSearch(q: string) {
    if (!data) return
    const lower = q.trim().toLowerCase()
    if (!lower) return
    // 1) Region match
    for (const [slug, meta] of Object.entries(REGIONS)) {
      if (slug === lower || meta.label.toLowerCase() === lower) {
        drillToRegion(slug as RegionSlug)
        return
      }
    }
    // 2) IL/PS combined alias bucket
    const IL_ALIASES = [
      'il',
      'ps',
      'israel',
      'palestine',
      'palestinian territories',
      'israel and palestinian territories',
    ]
    if (IL_ALIASES.includes(lower)) {
      drillToCountry('IL')
      return
    }
    // 3) ISO-2 direct
    if (lower.length === 2) {
      drillToCountry(lower.toUpperCase())
      return
    }
    // 4) Target name match (case-insensitive contains)
    const t = data.targets.find((t) => t.name.toLowerCase().includes(lower))
    if (t) {
      drillToSite(t.id)
      return
    }
    // 5) Incident ID exact match
    const inc = data.incidents.find((i) => i.id.toLowerCase() === lower)
    if (inc) {
      setOpenIncidentId(inc.id)
    }
  }

  function handleBreadcrumbNav(level: MapView) {
    if (level.kind === 'globe') return reset()
    if (level.kind === 'region') return drillToRegion(level.slug)
    if (level.kind === 'country') return drillToCountry(level.iso2)
    if (level.kind === 'site') return drillToSite(level.targetId)
  }

  const center = useMemo(() => {
    if (!data) return null
    if (view.kind === 'globe')
      return (
        <Globe
          data={data}
          onDrillCountry={drillToCountry}
          isLoading={query.isLoading}
          error={query.error}
        />
      )
    if (view.kind === 'region')
      return (
        <RegionMap
          data={data}
          region={view.slug}
          onDrillCountry={drillToCountry}
          isLoading={query.isLoading}
          error={query.error}
        />
      )
    if (view.kind === 'country')
      return (
        <CountryMap
          data={data}
          iso2={view.iso2}
          onDrillSite={drillToSite}
          isLoading={query.isLoading}
          error={query.error}
        />
      )
    if (view.kind === 'site')
      return <SiteView data={data} targetId={view.targetId} onBack={back} />
    return null
  }, [data, view, drillToCountry, drillToSite, back, query.isLoading, query.error])

  return (
    <div
      className="grid h-full min-h-0 grid-rows-[auto_1fr_auto] overflow-hidden bg-[color:var(--surface-primary)]"
      data-testid="map-next-page"
    >
      <h1 className="sr-only">SOC Threat Map</h1>
      {!socMode && (
        <div className="flex flex-col">
          <MapToolbar
            filters={filters}
            onFiltersChange={setFilters}
            view={view}
            onReset={reset}
            onSocToggle={() => setSocMode(true)}
            pulseRate={pulseRate}
            onPulseRateChange={setPulseRate}
            onRegionSelect={drillToRegion}
            onSearch={handleSearch}
          />
          {/* Plan B Plan 2 T17: ransomware activity overlay toggle */}
          <label
            className="group flex cursor-pointer items-center gap-2 border-b border-[color:var(--border-subtle)] bg-[color:var(--surface-secondary)] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-tertiary)] transition-colors hover:text-[color:var(--text-secondary)]"
            data-testid="map-ransomware-overlay-toggle"
          >
            <input
              type="checkbox"
              checked={ransomwareEnabled}
              onChange={(e) => setRansomwareEnabled(e.target.checked)}
              aria-label="Toggle ransomware leak-site activity overlay"
              className="h-3 w-3 accent-[color:var(--interactive-primary)]"
            />
            <span
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{
                background: ransomwareEnabled
                  ? 'var(--severity-critical)'
                  : 'var(--text-tertiary)',
              }}
              aria-hidden
            />
            <span>Ransomware leak-site activity</span>
          </label>
        </div>
      )}
      {socMode && data ? (
        <SocWallboardMode
          data={data}
          coverage={coverageQuery.data}
          coverageLoading={coverageQuery.isLoading}
          coverageError={coverageQuery.error}
          rangeDays={filters.rangeDays}
          feedAgeMs={feedAgeMs}
          onExit={() => setSocMode(false)}
          onDrillCountry={drillToCountry}
        />
      ) : (
        <div
          className={cn(
            'soc-stage grid min-h-0 gap-0 overflow-hidden max-[1100px]:grid-cols-[1fr]',
            // Dynamic column tracks depend on collapsed state. When a side is
            // collapsed, its column shrinks to a 24px strip so the chevron stays
            // visible + clickable; the centre track absorbs the freed space.
            leftCollapsed && rightCollapsed
              ? 'grid-cols-[24px_1fr_24px]'
              : leftCollapsed
                ? 'grid-cols-[24px_1fr_320px] max-[1400px]:grid-cols-[24px_1fr_280px]'
                : rightCollapsed
                  ? 'grid-cols-[280px_1fr_24px] max-[1400px]:grid-cols-[240px_1fr_24px]'
                  : 'grid-cols-[280px_1fr_320px] max-[1400px]:grid-cols-[240px_1fr_280px]',
          )}
        >
        {leftCollapsed ? (
          <aside
            data-testid="map-left-aside"
            data-collapsed="true"
            className="relative flex min-h-0 flex-col items-center gap-3 overflow-hidden border-r border-[color:var(--border-subtle)] py-3 max-[1100px]:hidden"
          >
            <button
              type="button"
              onClick={() => setLeftCollapsed(false)}
              data-testid="map-left-toggle"
              aria-label="Expand left panels"
              aria-expanded="false"
              className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-secondary)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--surface-tertiary)] hover:text-[color:var(--interactive-primary)]"
            >
              <ChevronRight className="h-3.5 w-3.5" aria-hidden />
            </button>
            <span
              className="soc-rail-label select-none font-mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--text-tertiary)]"
              aria-hidden
            >
              Intel Panels
            </span>
          </aside>
        ) : (
          <aside
            data-testid="map-left-aside"
            data-collapsed="false"
            className="relative flex min-h-0 flex-col overflow-hidden border-r border-[color:var(--border-subtle)] max-[1100px]:hidden"
          >
            <div className="flex shrink-0 items-center justify-between px-2 pt-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[color:var(--text-tertiary)]">
                Intel Panels
              </span>
              <button
                type="button"
                onClick={() => setLeftCollapsed(true)}
                data-testid="map-left-toggle"
                aria-label="Collapse left panels"
                aria-expanded="true"
                className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-secondary)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--surface-tertiary)] hover:text-[color:var(--interactive-primary)]"
              >
                <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-2 pb-2 pt-2">
              {data && (
                <TopAttackersPanel
                  actors={data.actors}
                  incidents={data.incidents}
                  onActorClick={(id) => setOpenActorId(id)}
                />
              )}
              {data && (
                <TopSectorsPanel
                  targets={data.targets}
                  incidents={data.incidents}
                  onSectorClick={(s) => setFilters((f) => ({ ...f, sectors: new Set([s]) }))}
                />
              )}
              {data && (
                <SeverityChartPanel
                  incidents={data.incidents}
                  filters={filters}
                  onToggleSeverity={(s) =>
                    setFilters((f) => {
                      const next = new Set(f.severities)
                      if (next.has(s)) next.delete(s)
                      else next.add(s)
                      return { ...f, severities: next }
                    })
                  }
                />
              )}
            </div>
          </aside>
        )}
        <main className="relative min-h-0 overflow-hidden">
          <Breadcrumbs view={view} onNavigate={handleBreadcrumbNav} />
          {center}
          {/* Plan B Plan 2 T16/T17 — ransomware leak-site activity overlay,
              hidden until the user toggles it on (defaults OFF). */}
          <RansomwareActivityOverlay enabled={ransomwareEnabled} fromTo={{}} />
        </main>
        {rightCollapsed ? (
          <aside
            data-testid="map-right-aside"
            data-collapsed="true"
            className="relative flex min-h-0 flex-col items-center gap-3 overflow-hidden border-l border-[color:var(--border-subtle)] py-3 max-[1100px]:hidden"
          >
            <button
              type="button"
              onClick={() => setRightCollapsed(false)}
              data-testid="map-right-toggle"
              aria-label="Expand live feed"
              aria-expanded="false"
              className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-secondary)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--surface-tertiary)] hover:text-[color:var(--interactive-primary)]"
            >
              <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
            </button>
            <span
              className="soc-rail-label select-none font-mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--text-tertiary)]"
              aria-hidden
            >
              Live Feed
            </span>
          </aside>
        ) : (
          <aside
            data-testid="map-right-aside"
            data-collapsed="false"
            className="relative flex min-h-0 flex-col overflow-hidden border-l border-[color:var(--border-subtle)] max-[1100px]:hidden"
          >
            <div className="flex shrink-0 items-center px-2 pt-2">
              <button
                type="button"
                onClick={() => setRightCollapsed(true)}
                data-testid="map-right-toggle"
                aria-label="Collapse live feed"
                aria-expanded="true"
                className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-secondary)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--surface-tertiary)] hover:text-[color:var(--interactive-primary)]"
              >
                <ChevronRight className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-2 pb-2 pt-2">
              {data && (
                <IncidentTicker
                  incidents={data.incidents}
                  actors={data.actors}
                  targets={data.targets}
                  onIncidentClick={(id) => setOpenIncidentId(id)}
                  pulseRate={pulseRate}
                />
              )}
            </div>
          </aside>
        )}
        </div>
      )}
      {!socMode && data && (
        <StatusBar
          meta={data.meta}
          analyst="OPS-04"
          shift="08:00-20:00"
          priority="HIGH"
          feedAgeMs={feedAgeMs}
        />
      )}
      {openIncident && (
        <IncidentDrawer
          incident={openIncident}
          actor={
            openActor ??
            (data?.actors.find((a) => a.id === openIncident.actorId) ?? null)
          }
          target={data?.targets.find((t) => t.id === openIncident.targetId) ?? null}
          onClose={() => setOpenIncidentId(null)}
          onOpenActor={(id) => {
            setOpenIncidentId(null)
            setOpenActorId(id)
          }}
        />
      )}
      {openActor && (
        <ActorDrawer
          actor={openActor}
          incidents={data?.incidents.filter((i) => i.actorId === openActor.id) ?? []}
          onClose={() => setOpenActorId(null)}
          onOpenIncident={(id) => {
            setOpenActorId(null)
            setOpenIncidentId(id)
          }}
        />
      )}
    </div>
  )
}
